import React, { useState, useEffect } from "react";
import {
  BarChart3,
  DollarSign,
  Calendar,
  FileText,
  Activity,
  User,
  CreditCard,
  Package,
} from "lucide-react";
import DetailedReportModal from "./DetailedReportModal";
import { useReports } from "./useReports";
import { quickRange } from "./quickRange";
import { useSelector } from "react-redux";
import { format, parseISO } from "date-fns";
import type { RootState } from "app/redux/store";
import { shopTimeZoneFromShops } from "app/utils/timezone";

const Reports: React.FC = () => {
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();
  const [quickTimeframe, setQuickTimeframe] = useState("7days");
  const [selectedReport, setSelectedReport] = useState<string | null>(null);

  const reports = useReports(startDate, endDate);

  const overview = reports?.overview || {};
  const serviceReport = reports?.serviceReport || [];
  const productReport = reports?.productReport || [];
  const staffReport = reports?.staffReport || [];

  const formatMoney = (val?: number) => `$${(val || 0).toFixed(2)}`;

  const shops = useSelector((state: RootState) => state.shops) as any[];
  const timeZoneId = shopTimeZoneFromShops(shops);

  // Convert quick filter to the shop's days
  useEffect(() => {
    if (quickTimeframe !== "custom") {
      const range = quickRange(quickTimeframe, new Date(), timeZoneId);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  }, [quickTimeframe, timeZoneId]);

  const handleQuickTimeframeChange = (value: string) => {
    if (value === "custom") {
      setShowCustomDate(true);
      setQuickTimeframe("custom");
    } else {
      setShowCustomDate(false);
      setQuickTimeframe(value);
    }
  };

  const handleApplyCustomDate = () => {
    if (startDate && endDate) setShowCustomDate(false);
  };

  const getDateRangeLabel = () => {
    if (startDate && endDate && quickTimeframe === "custom") {
      // parseISO keeps "2026-10-28" on that day; new Date() read it as UTC midnight, the day before in the US
      return `${format(parseISO(startDate), "M/d/yyyy")} - ${format(parseISO(endDate), "M/d/yyyy")}`;
    }

    switch (quickTimeframe) {
      case "30days":
        return "Last 30 days";
      case "90days":
        return "Last 90 days";
      case "12months":
        return "Last 12 months";
      default:
        return "Last 7 days";
    }
  };

  const standardReports = [
    {
      id: "revenue",
      title: "Sales Report",
      description: "Revenue, tips, discounts, tax breakdown",
      icon: DollarSign,
      color: "bg-green-500",
      data: reports?.salesReport || [],
    },
    {
      id: "services",
      title: "Services Report",
      description: "Complete list of all services and performance",
      icon: Activity,
      color: "bg-blue-500",
      data: reports?.serviceReport || [],
    },
    {
      id: "products",
      title: "Products Report",
      description: "Complete list of all products and sales",
      icon: Package,
      color: "bg-teal-500",
      data: reports?.productReport || [],
    },
    {
      id: "staff",
      title: "Staff Performance",
      description: "Individual metrics and revenue",
      icon: User,
      color: "bg-purple-500",
      data: reports?.staffReport || [],
    },
  ];

  return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between">
          <div className="flex space-x-3">
            <BarChart3 className="w-6 h-6 text-purple-600" />
            <div>
              <h1 className="text-2xl font-semibold">Reports & Analytics</h1>
              <p className="text-gray-600">
                Comprehensive business insights and analytics
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-600" />

            <select
                value={quickTimeframe}
                onChange={(e) => handleQuickTimeframeChange(e.target.value)}
                className="border rounded-lg px-3 py-2"
            >
              <option value="7days">Last 7 days</option>
              <option value="30days">Last 30 days</option>
              <option value="90days">Last 90 days</option>
              <option value="12months">Last 12 months</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>
        </div>

        {/* Custom date picker */}
        {showCustomDate && (
            <div className="bg-white border rounded-lg p-6">
              <div className="grid grid-cols-2 gap-4">
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="border rounded-lg px-3 py-2"
                />

                <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="border rounded-lg px-3 py-2"
                />
              </div>

              <button
                  onClick={handleApplyCustomDate}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
              >
                Apply Date Range
              </button>
            </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-6">
          <Card
              title="Total Revenue"
              value={formatMoney(overview.totalRevenue)}
              icon={<DollarSign />}
          />

          <Card
              title="Total Tips"
              value={formatMoney(overview.totalTips)}
              icon={<User />}
          />

          <Card
              title="Service Revenue"
              value={formatMoney(overview.serviceRevenue)}
              icon={<CreditCard />}
          />

          <Card
              title="Product Revenue"
              value={formatMoney(overview.productRevenue)}
              icon={<Package />}
          />
        </div>

        {/* Standard Reports */}
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="w-6 h-6 text-blue-600" />
            <h3 className="text-lg font-medium">Standard Reports</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {standardReports.map((report) => {
              const Icon = report.icon;

              return (
                  <div
                      key={report.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow hover:border-blue-300"
                  >
                    <div className="flex items-center justify-between">

                      <div className="flex items-center space-x-3">
                        <div
                            className={`w-10 h-10 ${report.color} rounded-lg flex items-center justify-center`}
                        >
                          <Icon className="w-5 h-5 text-white" />
                        </div>

                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {report.title}
                          </h4>

                          <p className="text-sm text-gray-600">
                            {report.description}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">

                        <p className="text-sm font-medium text-gray-900">
                          {report.data.length} records
                        </p>

                        <button
                            onClick={() => setSelectedReport(report.id)}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View Report →
                        </button>

                      </div>

                    </div>
                  </div>
              );
            })}
          </div>
        </div>

        <DetailedReportModal
            isOpen={selectedReport !== null}
            onClose={() => setSelectedReport(null)}
            reportType={selectedReport || ""}
            reportData={
              selectedReport === "revenue"
                  ? reports?.salesReport
                  : selectedReport === "services"
                      ? reports?.serviceReport
                      : selectedReport === "products"
                          ? reports?.productReport
                          : reports?.staffReport
            }
            dateRange={getDateRangeLabel()}
        />
      </div>
  );
};

const Card = ({
                title,
                value,
                icon,
              }: {
  title: string;
  value: string;
  icon: any;
}) => (
    <div className="bg-white border rounded-lg p-6 flex justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
      </div>

      <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white">
        {icon}
      </div>
    </div>
);

export default Reports;